// src/pages/announcements/AddAnnouncement.tsx
import React from 'react';
import { DynamicField, DynamicFieldTypes } from 'src/components/dynamic-widget';
import DynamicFormWidget from 'src/components/dynamic-widget/DynamicFormWidget';
import { DataService } from 'src/services/data-service';
import GenericDialog from 'src/components/GenericDialog';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { AnnouncementLevel, IAnnouncement } from '.';
import { FileType } from 'src/models/file';

import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';

interface IAddAnnouncementProps {
  onSuccess: () => void;
  onClose: () => void;
  announcement?: IAnnouncement | null;
}

const languages = {
  en: 'English',
  ar: 'Arabic',
  fr: 'French',
};

const levelItems: Record<string, string> = {
  [AnnouncementLevel.Info]: 'Info',
  [AnnouncementLevel.Advisory]: 'Advisory',
  [AnnouncementLevel.Important]: 'Important',
  [AnnouncementLevel.Critical]: 'Critical',
};

const mapFileTypesToAccept = (types: string[]) => types.join(',');

const ALL_ALLOWED_EXTENSIONS = [
  `.${FileType.jpg}`, `.${FileType.jpeg}`, `.${FileType.png}`, `.${FileType.gif}`, `.${FileType.svg}`,
  `.${FileType.pdf}`, `.${FileType.doc}`, `.${FileType.docx}`,
  `.${FileType.xls}`, `.${FileType.xlsx}`,
  `.${FileType.csv}`, `.${FileType.msg}`, `.${FileType.eml}`,
];

function bytesToMb(b: number) { return b / (1024 * 1024); }

type AttachmentManagerProps = {
  value: File[];
  onChange: (files: File[]) => void;
  title?: string;
  disabled?: boolean;
  allowedTypes?: string[];
  maxTotalSizeMb?: number;
  maxFiles?: number;
};

const AttachmentManager: React.FC<AttachmentManagerProps> = ({
  value,
  onChange,
  title = 'Additional Attachments (Documents, Images, etc.)',
  disabled,
  allowedTypes = ALL_ALLOWED_EXTENSIONS,
  maxTotalSizeMb = 200,
  maxFiles = 50,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const accept = React.useMemo(() => mapFileTypesToAccept(allowedTypes), [allowedTypes]);
  const currentTotalSize = React.useMemo(
    () => value.reduce((acc, f) => acc + f.size, 0),
    [value]
  );

  const dedupe = (files: File[]) => {
    const map = new Map<string, File>();
    [...value, ...files].forEach((f) => {
      const key = `${f.name}-${f.size}-${f.lastModified}`;
      if (!map.has(key)) map.set(key, f);
    });
    return Array.from(map.values());
  };

  const filterByAccept = (files: File[]) => {
    if (!accept) return files;
    const accepted = accept.split(',').map(a => a.trim().toLowerCase());
    return files.filter(f => {
      const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
      return accepted.includes(ext);
    });
  };

  const constrain = (files: File[]) => {
    let next = files;

    if (next.length > maxFiles) {
      next = next.slice(0, maxFiles);
    }

    const totalMb = bytesToMb(next.reduce((acc, f) => acc + f.size, 0));
    if (totalMb > maxTotalSizeMb) {
      const keep: File[] = [];
      let size = 0;
      for (const f of next) {
        if (bytesToMb(size + f.size) <= maxTotalSizeMb) {
          keep.push(f);
          size += f.size;
        } else break;
      }
      next = keep;
    }
    return next;
  };

  const handleFiles = (filesList: FileList | null) => {
    if (!filesList || disabled) return;
    const incoming = Array.from(filesList);
    const filtered = filterByAccept(incoming);
    const deduped = dedupe(filtered);
    const constrained = constrain(deduped);
    onChange(constrained);
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const removeAt = (idx: number) => {
    const copy = [...value];
    copy.splice(idx, 1);
    onChange(copy);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderStyle: 'dashed',
          ...(isDragging ? { backgroundColor: 'action.hover' } : {}),
        }}
        onDragOver={(e) => (e.preventDefault(), setIsDragging(true))}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            Add files
          </Button>
          <Typography variant="body2" color="text.secondary">
            or drag & drop here
          </Typography>
        </Stack>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          hidden
          onChange={(e) => handleFiles(e.target.files)}
        />
      </Paper>

      {value.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {value.length} file{value.length > 1 ? 's' : ''} • {bytesToMb(currentTotalSize).toFixed(1)} MB
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
            {value.map((f, idx) => (
              <Chip
                key={`${f.name}-${f.size}-${f.lastModified}`}
                icon={<AttachFileIcon />}
                label={f.name}
                onDelete={disabled ? undefined : () => removeAt(idx)}
                deleteIcon={<DeleteIcon />}
                variant="outlined"
                sx={{ maxWidth: 280 }}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

const AddAnnouncement: React.FC<IAddAnnouncementProps> = ({ onSuccess, onClose, announcement }) => {
  const dispatch = useAppDispatch();
  const isEditMode = !!announcement;

  // Local state for multi-attachments (outside DynamicFormWidget)
  const [attachments, setAttachments] = React.useState<File[]>([]);

  const formConfig = React.useMemo((): Record<string, DynamicFieldTypes> => {
    const baseConfig: Record<string, DynamicFieldTypes> = {
      title: {
        type: DynamicField.TEXT,
        required: true,
        name: 'title',
        title: 'Title',
        value: announcement?.title || '',
        disabled: false
      },
      body: {
        type: DynamicField.TEXTAREA,
        required: true,
        name: 'body',
        title: 'Body',
        value: announcement?.body || '',
        disabled: false
      },
      language: {
        type: DynamicField.SELECT,
        required: true,
        name: 'language',
        title: 'Language',
        value: announcement?.language || '',
        disabled: false,
        items: languages
      },
      level: {
        type: DynamicField.SELECT,
        required: true,
        name: 'level',
        title: 'Level',
        value: announcement?.level || AnnouncementLevel.Info,
        disabled: false,
        items: levelItems
      },
      isPopup: {
        type: DynamicField.CHECKBOX,
        required: false,
        name: 'isPopup',
        title: 'Show as Popup',
        value: announcement?.isPopup || false,
        disabled: false
      },
      image: {
        type: DynamicField.FILE,
        required: false,
        name: 'image',
        title: 'Announcement Image',
        value: announcement?.imageUrl || '',
        disabled: false,
        allowedTypes: [
          FileType.jpg, FileType.jpeg, FileType.png, FileType.gif, FileType.svg
        ]
      },
      // Note: old single-file "attachments" field stays removed.
    };

    // ➕ Only add SendAsEmail when creating (no announcement yet)
    if (!isEditMode) {
      baseConfig.sendAsEmail = {
        type: DynamicField.CHECKBOX,
        required: false,
        name: 'sendAsEmail',
        title: 'Send as Email',
        value: false,
        disabled: false
      };
    }

    return baseConfig;
  }, [announcement, isEditMode]);

  const submit = React.useCallback(async (data: Record<string, any>) => {
    try {
      const endpoint = isEditMode ? 'api/Announcement/update' : 'api/Announcement/create';

      // Create FormData for the main announcement (title/body/etc + optional image)
      const formData = new FormData();

      formData.append('Title', data.title || '');
      formData.append('Body', data.body || '');
      formData.append('Language', data.language || '');
      formData.append('Level', data.level || '');
      formData.append('IsPopup', String(data.isPopup || false));

      // Only send SendAsEmail on create
      if (!isEditMode) {
        formData.append('SendAsEmail', String(data.sendAsEmail || false));
      }

      if (isEditMode) {
        formData.append('Id', announcement!.id);
        // keep existing image when editing if no new image was chosen
        if (announcement!.imageUrl && !(data.image instanceof File)) {
          formData.append('ImageUrl', announcement!.imageUrl);
        }
      }

      if (data.image instanceof File) {
        formData.append('Image', data.image);
      }

      const response = await DataService.postForm(endpoint, formData);

      if (!response.ok) {
        const result: { message?: string } = await response.json();
        dispatch(showAlert({
          message: result?.message || 'An error occurred!',
          type: 'error'
        }));
        return false;
      }

      // parse the created/updated id
      const result = await response.json();
      const announcementId = isEditMode ? announcement!.id : result?.id;

      // Bulk-upload attachments (if any) – same behaviour for create & update
      if (attachments.length > 0 && announcementId) {
        const attachmentFormData = new FormData();
        attachmentFormData.append('EntityType', 'Announcement');
        attachmentFormData.append('EntityId', announcementId);

        for (const file of attachments) {
          attachmentFormData.append('Files', file);
        }

        const attachmentResponse = await DataService.postForm(
          'api/Attachment/upload-bulk',
          attachmentFormData
        );

        if (!attachmentResponse.ok) {
          dispatch(showAlert({
            message: 'Announcement saved but failed to upload some attachments',
            type: 'warning'
          }));
        }
      }

      onSuccess();
      return true;
    } catch (error) {
      dispatch(showAlert({
        message: 'An unexpected error occurred!',
        type: 'error'
      }));
      return false;
    }
  }, [dispatch, onSuccess, isEditMode, announcement, attachments]);

  return (
    <GenericDialog
      open={true}
      title={isEditMode ? 'Edit Announcement' : 'Add Announcement'}
      onClose={onClose}
    >
      <Stack spacing={2}>
        {/* Inline multi-attachment manager (chips/tags) */}
        <AttachmentManager
          value={attachments}
          onChange={setAttachments}
          allowedTypes={ALL_ALLOWED_EXTENSIONS}
          maxFiles={50}
          maxTotalSizeMb={200}
        />

        {/* Dynamic form */}
        <DynamicFormWidget
          title=""
          fields={formConfig}
          onSubmit={submit}
        />
      </Stack>
    </GenericDialog>
  );
};

export default AddAnnouncement;
